/**
 * Standardized error handling utility for TeeSync services
 */

export const ErrorCodes = {
  // Authentication errors
  AUTH_REQUIRED: 'AUTH_REQUIRED',
  AUTH_FAILED: 'AUTH_FAILED',
  
  // Group errors
  GROUP_NOT_FOUND: 'GROUP_NOT_FOUND',
  INVALID_INVITE_CODE: 'INVALID_INVITE_CODE',
  ALREADY_MEMBER: 'ALREADY_MEMBER',
  INSUFFICIENT_PERMISSIONS: 'INSUFFICIENT_PERMISSIONS',
  PERMISSION_DENIED: 'PERMISSION_DENIED',

  // Plan / subscription gates (caps trip these; client surfaces a paywall)
  PLAN_CAP_GROUPS: 'PLAN_CAP_GROUPS',
  PLAN_CAP_GROUP_SIZE: 'PLAN_CAP_GROUP_SIZE',
  PLAN_CAP_FRIENDS: 'PLAN_CAP_FRIENDS',
  PLAN_CAP_PLANNED_ROUNDS: 'PLAN_CAP_PLANNED_ROUNDS',
  
  // Round errors
  ROUND_NOT_FOUND: 'ROUND_NOT_FOUND',
  SCHEDULING_TIMEOUT: 'SCHEDULING_TIMEOUT',
  INVALID_ROUND_DATA: 'INVALID_ROUND_DATA',
  
  // Availability errors
  AVAILABILITY_TIMEOUT: 'AVAILABILITY_TIMEOUT',
  INVALID_TIME_RANGE: 'INVALID_TIME_RANGE',
  
  // General errors
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  NETWORK_ERROR: 'NETWORK_ERROR',
  DATABASE_ERROR: 'DATABASE_ERROR',
  TIMEOUT_ERROR: 'TIMEOUT_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR'
}

/**
 * Creates a standardized error response
 * @param {string} operation - The operation that failed
 * @param {Error|object} error - The error object
 * @param {object} context - Additional context for the error
 * @returns {object} Standardized error response
 */
export const createErrorResponse = (operation, error, context = {}) => {
  const errorResponse = {
    message: error.message || 'An unknown error occurred',
    code: error.code || ErrorCodes.UNKNOWN_ERROR,
    operation,
    timestamp: new Date().toISOString(),
    ...context
  }
  
  // Log the error with context
  console.error(`Error in ${operation}:`, errorResponse)
  
  return { data: null, error: errorResponse }
}

/**
 * Creates a standardized success response
 * @param {any} data - The success data
 * @returns {object} Standardized success response
 */
export const createSuccessResponse = (data) => {
  return { data, error: null }
}

/**
 * Creates a custom error with a specific code and message
 * @param {string} code - Error code from ErrorCodes
 * @param {string} message - Error message
 * @param {object} details - Additional error details
 * @returns {Error} Custom error object
 */
export const createError = (code, message, details = {}) => {
  const error = new Error(message)
  error.code = code
  error.details = details
  return error
}

/**
 * Handles authentication errors consistently
 * @param {string} operation - The operation that failed
 * @returns {object} Standardized auth error response
 */
export const createAuthError = (operation) => {
  return createErrorResponse(
    operation,
    createError(ErrorCodes.AUTH_REQUIRED, 'Authentication required'),
    { requiresAuth: true }
  )
}

/**
 * Handles timeout errors consistently
 * @param {string} operation - The operation that timed out
 * @param {number} timeoutMs - Timeout duration in milliseconds
 * @returns {object} Standardized timeout error response
 */
export const createTimeoutError = (operation, timeoutMs) => {
  return createErrorResponse(
    operation,
    createError(ErrorCodes.TIMEOUT_ERROR, `Operation timed out after ${timeoutMs}ms`),
    { timeout: timeoutMs }
  )
}

/**
 * Handles validation errors consistently
 * @param {string} operation - The operation that failed validation
 * @param {string} field - The field that failed validation
 * @param {string} message - The validation error message
 * @returns {object} Standardized validation error response
 */
export const createValidationError = (operation, field, message) => {
  return createErrorResponse(
    operation,
    createError(ErrorCodes.VALIDATION_ERROR, message),
    { field, validationError: true }
  )
}

/**
 * Wrapper for database operations with consistent error handling
 * @param {string} operation - The operation being performed
 * @param {Function} dbOperation - The database operation function
 * @param {object} context - Additional context for error reporting
 * @returns {Promise<object>} Standardized response
 */
export const withErrorHandling = async (operation, dbOperation, context = {}) => {
  try {
    const result = await dbOperation()
    return createSuccessResponse(result)
  } catch (error) {
    // Check if it's a Supabase error and extract useful information
    if (error.code) {
      return createErrorResponse(operation, error, context)
    }
    
    // Handle generic errors
    const standardError = createError(
      ErrorCodes.DATABASE_ERROR,
      error.message || 'Database operation failed',
      { originalError: error }
    )
    
    return createErrorResponse(operation, standardError, context)
  }
}

/**
 * Wrapper for operations with timeout protection
 * @param {string} operation - The operation being performed
 * @param {Function} asyncOperation - The async operation function
 * @param {number} timeoutMs - Timeout in milliseconds
 * @param {object} context - Additional context for error reporting
 * @returns {Promise<object>} Standardized response
 */
export const withTimeout = async (operation, asyncOperation, timeoutMs, context = {}) => {
  const timeoutPromise = new Promise((_, reject) => {
    setTimeout(() => {
      reject(createError(
        ErrorCodes.TIMEOUT_ERROR,
        `Operation timed out after ${timeoutMs}ms`
      ))
    }, timeoutMs)
  })
  
  try {
    const result = await Promise.race([asyncOperation(), timeoutPromise])
    return createSuccessResponse(result)
  } catch (error) {
    return createErrorResponse(operation, error, { ...context, timeout: timeoutMs })
  }
}